import React, { useEffect, useState } from 'react'
import {
  Box, Typography, IconButton, Tooltip, Divider, Chip,
} from '@mui/material'
import CfpSelect from '../../comps/CfpSelect'
import { DataTable, type DataTableColumn, type DataTableSort } from '../../comps/DataTable'
import { AddTemplateDialog } from './ManageScoringTab'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import { useApp } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { FS_MD, FS_SM, FS_XS, ICON_MD, ICON_SM, COLOR_PRIMARY_HOVER2 } from '../../common/theme'
import ResizableDivider from '../../comps/ResizableDivider'
import { saveElement, deleteElement } from '../../services/TemplatesService'
import { pmInit } from '../../services/ProjectManagerService'
import InputModal from '../modals/InputModal'
import ConfirmModal from '../modals/ConfirmModal'
import type { Project, TaWinThemesTabManageProps } from '../../types'

function makeId() { return crypto.randomUUID() }

const SCOPE_ALL = '__all__'
const SCOPE_GLOBAL = '__global__'

const noUnderline = {
  '&:before': { borderBottom: 'none' },
  '&:after': { borderBottom: 'none' },
  '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
}

export default function ManageWinThemesTab({ winthemes, onRefresh, projectsha }: TaWinThemesTabManageProps) {
  const { projects: ctxProjects } = useApp()

  const [pickerProjects, setPickerProjects] = useState<Project[]>(ctxProjects)
  useEffect(() => {
    if (ctxProjects.length > 0) {
      setPickerProjects(ctxProjects)
      return
    }
    let cancelled = false
    pmInit(0, 'name', true, 500, '').then(result => {
      if (cancelled) return
      const list: Project[] = result?.projects ?? []
      setPickerProjects(list)
    }).catch(() => { /* ignore */ })
    return () => { cancelled = true }
  }, [ctxProjects])
  const projects = pickerProjects

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [sort, setSort] = useState<DataTableSort>({ colId: 'name', dir: 'asc' })
  // Bump to force a re-render after in-place edits - calling onRefresh would
  // refetch /api2/sminit and overwrite the local mutation.
  const [, bumpLocal] = useState(0)
  const tick = () => bumpLocal(n => n + 1)
  const [addOpen, setAddOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteSetOpen, setDeleteSetOpen] = useState(false)
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<any>(null)
  const [deleteCheckpointTarget, setDeleteCheckpointTarget] = useState<{ catId: string; index: number } | null>(null)
  const [lhsWidth, setLhsWidth] = useState(320)
  const [scope, setScope] = useState<string>(SCOPE_ALL)

  const scopeOptions = (() => {
    const shaSet = new Set<string>()
    for (const w of winthemes) if (w.projectsha) shaSet.add(w.projectsha)
    const projectOpts = Array.from(shaSet).map(sha => {
      const proj = projects.find(p => p.projectsha === sha)
      return { value: sha, label: proj?.name ?? `Project ${sha.slice(0, 8)}` }
    }).sort((a, b) => a.label.localeCompare(b.label))
    return [
      { value: SCOPE_ALL, label: 'All' },
      { value: SCOPE_GLOBAL, label: 'Global' },
      ...projectOpts,
    ]
  })()

  const visible = projectsha
    ? winthemes.filter(w => w.projectsha === projectsha)
    : (scope === SCOPE_ALL
      ? winthemes
      : scope === SCOPE_GLOBAL
        ? winthemes.filter(w => !w.projectsha)
        : winthemes.filter(w => w.projectsha === scope))

  const projectNameFor = (w: any) => {
    if (!w.projectsha) return ''
    return projects.find(p => p.projectsha === w.projectsha)?.name ?? ''
  }

  const sorted = [...visible].sort((a, b) => {
    let valA: string
    let valB: string
    if (sort.colId === 'scope') {
      valA = a.projectsha ? 'Project' : 'Global'
      valB = b.projectsha ? 'Project' : 'Global'
    } else if (sort.colId === 'project') {
      valA = projectNameFor(a)
      valB = projectNameFor(b)
    } else {
      valA = String(a.name ?? '')
      valB = String(b.name ?? '')
    }
    const cmp = valA.localeCompare(valB)
    return sort.dir === 'asc' ? cmp : -cmp
  })

  const winthemeCols: DataTableColumn<any>[] = [
    {
      id: 'name',
      label: 'Name',
      width: 140,
      flex: 1,
      minWidth: 80,
      render: (w) => (
        <Typography variant="caption" sx={{
          fontSize: FS_SM,
          fontWeight: w.id === selectedId ? 600 : 400,
          color: w.id === selectedId ? 'primary.main' : 'text.primary',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{w.name}</Typography>
      ),
    },
    {
      id: 'scope',
      label: 'Scope',
      width: 70,
      render: (w) => {
        const isGlobal = !w.projectsha
        return (
          <Chip
            size="small"
            label={isGlobal ? 'Global' : 'Project'}
            sx={{
              height: 16, fontSize: '0.6rem', maxWidth: '100%',
              bgcolor: isGlobal ? 'action.selected' : 'primary.main',
              color: isGlobal ? 'text.secondary' : 'primary.contrastText',
              '& .MuiChip-label': { px: 0.6, overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        )
      },
    },
    // Hidden in bid manager and when scope is Global (column would be empty).
    ...(projectsha || scope === SCOPE_GLOBAL ? [] : [{
      id: 'project',
      label: 'Project',
      width: 120,
      flex: 1,
      minWidth: 60,
      render: (w: any) => (
        <Typography variant="caption" sx={{
          fontSize: FS_XS, opacity: w.projectsha ? 1 : 0.45,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{projectNameFor(w)}</Typography>
      ),
    }]),
  ]

  const selectedTemplate = visible.find((s) => s.id === selectedId) ?? null

  // Win theme sets share the same backend endpoints as scoring templates,
  // distinguished by `type: 'wintheme'` on the TAElementGroup payload.
  //   POST /api2/besaveelement  - create / rename / save
  //   POST /api2/bedelelement   - delete

  const handleAddSet = async (name: string, targetProjectSha?: string) => {
    if (!name.trim()) return
    try {
      const sha = projectsha ?? targetProjectSha
      const body: Record<string, any> = {
        name: name.trim(),
        type: 'wintheme',
        categories: [],
      }
      if (sha) body.projectsha = sha
      await saveElement(body)
      addToast('success', 'Win theme set added.')
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to add win theme set.')
    }
  }

  const handleRenameSet = async (newname: string) => {
    if (!selectedTemplate || !newname.trim()) return
    try {
      selectedTemplate.name = newname.trim()
      await saveElement(selectedTemplate)
      addToast('success', 'Win theme set renamed.')
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to rename win theme set.')
    }
  }

  const handleDeleteSet = async () => {
    if (!selectedTemplate) return
    try {
      await deleteElement(selectedTemplate)
      addToast('success', 'Win theme set deleted.')
      setSelectedId(null)
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to delete win theme set.')
    }
  }

  const handleSaveSet = async () => {
    if (!selectedTemplate) return
    try {
      await saveElement(selectedTemplate)
      addToast('success', 'Win theme set saved.')
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to save win theme set.')
    }
  }

  const handleAddCategory = () => {
    if (!selectedTemplate) return
    selectedTemplate.categories = [
      ...(selectedTemplate.categories ?? []),
      { id: makeId(), category: '', checkpoints: [] },
    ]
    tick()
  }

  const handleDeleteCategory = () => {
    if (!deleteCategoryTarget || !selectedTemplate) return
    selectedTemplate.categories = (selectedTemplate.categories ?? []).filter(
      (c: any) => c.id !== deleteCategoryTarget.id
    )
    setDeleteCategoryTarget(null)
    tick()
  }

  const handleAddCheckpoint = (cat: any) => {
    cat.checkpoints = [...(cat.checkpoints ?? []), '']
    tick()
  }

  const handleDeleteCheckpoint = () => {
    if (!deleteCheckpointTarget || !selectedTemplate) return
    const cat = (selectedTemplate.categories ?? []).find((c: any) => c.id === deleteCheckpointTarget.catId)
    if (cat) cat.checkpoints = (cat.checkpoints ?? []).filter((_: string, i: number) => i !== deleteCheckpointTarget.index)
    setDeleteCheckpointTarget(null)
    tick()
  }

  const handleCheckpointChange = (cat: any, index: number, value: string) => {
    if (!cat.checkpoints) return
    cat.checkpoints[index] = value
  }

  return (
    <Box data-testid="bmw_root" sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, flex: 1 }}>

      {/* Modals */}
      {projectsha ? (
        <InputModal
          open={addOpen}
          title="Add Win Theme Set"
          okLabel="Add"
          placeholder="Set name"
          singleLine
          onConfirm={(name) => handleAddSet(name)}
          onClose={() => setAddOpen(false)}
        />
      ) : (
        <AddTemplateDialog
          open={addOpen}
          title="Add Win Theme Set"
          projects={projects}
          defaultProjectSha={scope === SCOPE_GLOBAL ? '' : scope}
          onConfirm={(name, sha) => handleAddSet(name, sha || undefined)}
          onClose={() => setAddOpen(false)}
        />
      )}
      <InputModal
        open={renameOpen}
        title="Rename Win Theme Set"
        okLabel="Rename"
        placeholder="New name"
        singleLine
        onConfirm={handleRenameSet}
        onClose={() => setRenameOpen(false)}
      />
      <ConfirmModal
        open={deleteSetOpen}
        title="Delete Win Theme Set"
        body={`Are you sure you want to delete "${selectedTemplate?.name}"?`}
        okLabel="Delete"
        onConfirm={handleDeleteSet}
        onClose={() => setDeleteSetOpen(false)}
      />
      <ConfirmModal
        open={!!deleteCategoryTarget}
        title="Delete Category"
        body={`Are you sure you want to delete "${deleteCategoryTarget?.category}"?`}
        okLabel="Delete"
        onConfirm={handleDeleteCategory}
        onClose={() => setDeleteCategoryTarget(null)}
      />
      <ConfirmModal
        open={!!deleteCheckpointTarget}
        title="Delete Win Theme"
        body="Are you sure you want to delete this win theme?"
        okLabel="Delete"
        onConfirm={handleDeleteCheckpoint}
        onClose={() => setDeleteCheckpointTarget(null)}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>Win Theme Sets</Typography>
        <Tooltip title="Refresh">
          <IconButton data-testid="bmw_refresh_btn" size="small" onClick={onRefresh}><RefreshIcon sx={{ fontSize: ICON_MD }} /></IconButton>
        </Tooltip>
        <Tooltip title="Add Win Theme Set">
          <IconButton data-testid="bmw_add_set_btn" size="small" onClick={() => setAddOpen(true)}><AddIcon sx={{ fontSize: ICON_MD }} /></IconButton>
        </Tooltip>
      </Box>
      <Divider />

      {/* Split panel */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* LHS list */}
        <Box sx={{ width: lhsWidth, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!projectsha && (
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.5 }}>
              <CfpSelect
                label="Scope"
                value={scope}
                options={scopeOptions}
                onChange={setScope}
                testId="bmw_scope_picker"
              />
            </Box>
          )}
          <DataTable
            rows={sorted}
            cols={winthemeCols}
            rowKey={(s, i) => s.id ?? i}
            rowTestId="bmw_set_row"
            rowDataAttrs={(s) => ({ 'data-set-id': s.id })}
            onRowClick={(s) => setSelectedId(s.id)}
            rowSx={(s) => s.id === selectedId ? { bgcolor: COLOR_PRIMARY_HOVER2 } : {}}
            sort={sort}
            onSortChange={(colId, dir) => setSort({ colId, dir })}
            testIdPrefix="bmw_table"
          />
        </Box>
        {/* Panel divider - drag to resize the LHS list */}
        <ResizableDivider onDelta={(dx) => setLhsWidth(w => Math.max(140, Math.min(500, w + dx)))} />

        {/* RHS */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            {selectedTemplate ? (
              <Chip size="small" icon={<LayersOutlinedIcon />} label={selectedTemplate.name} sx={{ mr: 'auto', fontSize: FS_XS }} />
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            <Tooltip title="Save Win Theme Set">
              <span>
                <IconButton data-testid="bmw_save_set_btn" size="small" disabled={!selectedTemplate} onClick={handleSaveSet}><SaveIcon sx={{ fontSize: ICON_MD }} /></IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Rename Win Theme Set">
              <span>
                <IconButton data-testid="bmw_rename_set_btn" size="small" disabled={!selectedTemplate} onClick={() => setRenameOpen(true)}><EditIcon sx={{ fontSize: ICON_MD }} /></IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Win Theme Set">
              <span>
                <IconButton data-testid="bmw_delete_set_btn" size="small" disabled={!selectedTemplate} onClick={() => setDeleteSetOpen(true)}><DeleteOutlineIcon sx={{ fontSize: ICON_MD }} /></IconButton>
              </span>
            </Tooltip>
          </Box>

          {selectedTemplate ? (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>

              {/* Categories */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>Categories</Typography>
                <Tooltip title="Add Category">
                  <IconButton size="small" onClick={handleAddCategory}><AddIcon sx={{ fontSize: ICON_SM }} /></IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 1 }} />

              {(selectedTemplate.categories ?? []).length === 0 && (
                <Typography variant="caption" sx={{ opacity: 0.45, display: 'block', px: 0.5, mb: 1 }}>
                  No categories. Click + to add one.
                </Typography>
              )}

              {(selectedTemplate.categories ?? []).map((cat: any) => (
                <Box key={cat.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, overflow: 'hidden' }}>

                  {/* Category header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, bgcolor: 'action.hover', gap: 0.5 }}>
                    <Box
                      component="input"
                      placeholder="Category name"
                      defaultValue={cat.category ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => { cat.category = e.target.value }}
                      sx={{
                        flex: 1, border: 'none', outline: 'none', bgcolor: 'transparent',
                        fontSize: FS_SM, fontWeight: 600, py: 0.25, px: 0.5,
                        ...noUnderline,
                      }}
                    />
                    <Tooltip title="Add Win Theme">
                      <IconButton size="small" onClick={() => handleAddCheckpoint(cat)} sx={{ p: 0.25 }}>
                        <AddIcon sx={{ fontSize: ICON_SM }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Category">
                      <IconButton size="small" onClick={() => setDeleteCategoryTarget(cat)} sx={{ p: 0.25, opacity: 0.55, '&:hover': { opacity: 1 } }}>
                        <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Win theme rows */}
                  <Box sx={{ p: 0.75 }}>
                    {(cat.checkpoints ?? []).length === 0 && (
                      <Typography variant="caption" sx={{ opacity: 0.4, display: 'block', px: 0.5 }}>No win themes</Typography>
                    )}
                    {(cat.checkpoints ?? []).map((check: string, i: number) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 0.5, gap: 0.5 }}>
                        <Box
                          component="span"
                          contentEditable
                          suppressContentEditableWarning
                          data-placeholder="Win Theme"
                          onBlur={(e: React.FocusEvent<HTMLSpanElement>) => handleCheckpointChange(cat, i, e.currentTarget.textContent ?? '')}
                          dangerouslySetInnerHTML={{ __html: check ?? '' }}
                          sx={{
                            flex: 1, fontSize: FS_MD, lineHeight: 1.5, px: 0.5,
                            border: '1px solid transparent', borderRadius: 0.5,
                            '&:focus': { outline: 'none', borderColor: 'primary.main', borderStyle: 'solid' },
                            '&:empty::before': { content: 'attr(data-placeholder)', color: 'text.disabled', pointerEvents: 'none' },
                          }}
                        />
                        <Tooltip title="Delete Win Theme">
                          <IconButton size="small" onClick={() => setDeleteCheckpointTarget({ catId: cat.id, index: i })} sx={{ opacity: 0.45, '&:hover': { opacity: 1 } }}>
                            <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.4 }}>Select a win theme set</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

